// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { TRAYS, WEIGHT_UNITS, utils } from '@seedplanner/shared-utils';
import Decimal from 'decimal.js';
import type * as React from 'react';
import type { Props as NewCropOrBlendToUseFormProps } from './NewCropOrBlendToUseForm';
import { getSizeParamsFromKey } from './sizeUtils';

interface CropData {
  readonly id: string;
  readonly crop_name: string;
  readonly yield_per_tray: string;
  readonly yield_unit: string;
  readonly total_grow_time: number;
  readonly buffer_percentage: number;
  readonly harvest_buffer_time: number;
}

const getTraysFromCropForms = (cropForms: React.ReactElement<NewCropOrBlendToUseFormProps>[]) => {
  const cropFormValues = cropForms.map((form) => form.props.values);
  const cropMap = new Map<string, { readonly trayCount: Decimal; readonly cropData: CropData }>();
  for (const formValues of cropFormValues) {
    if (formValues.cropOrBlendSelected == null) {
      continue;
    }
    if (formValues.cropOrBlendSelected.type === 'blend') {
      continue;
    }
    const { itemCount, itemCountValidation, countUnit, cropOrBlendSelected: cropSelected } = formValues;
    const itemCountNum = Number.parseFloat(itemCount) || 0;

    const maybeExistingCrop = cropMap.get(cropSelected.id);
    const existingCount = maybeExistingCrop?.trayCount ?? new Decimal(0);
    if (itemCountValidation !== '' || itemCountNum <= 0) {
      cropMap.set(cropSelected.id, {
        trayCount: maybeExistingCrop?.trayCount ?? new Decimal(0),
        cropData: cropSelected,
      });
      continue;
    }

    const { size, unit } = getSizeParamsFromKey(countUnit);
    const realItemCount = Decimal.mul(itemCountNum, size || 1).toNumber(); // Total ounces or grams;

    if (unit === TRAYS) {
      const newCount = existingCount.add(new Decimal(realItemCount));
      cropMap.set(cropSelected.id, {
        trayCount: newCount,
        cropData: cropSelected,
      });
    } else if (unit === WEIGHT_UNITS.GRAMS || unit === WEIGHT_UNITS.OUNCES) {
      const yieldPerTrayCountUnit = utils.convert({
        inputUnit: cropSelected.yield_unit,
        outputUnit: unit,
        quantity: Number.parseFloat(cropSelected.yield_per_tray),
      });
      const traysNeeded = Decimal.div(realItemCount, yieldPerTrayCountUnit);
      const newCount = existingCount.add(traysNeeded);
      cropMap.set(cropSelected.id, {
        trayCount: newCount,
        cropData: cropSelected,
      });
    }
  }

  return cropMap;
};

const getTraysFromBlendForms = (blendForms: React.ReactElement<NewCropOrBlendToUseFormProps>[]) => {
  const blendFormValues = blendForms.map((form) => form.props.values);
  const cropMap = new Map<string, { readonly trayCount: Decimal; readonly cropData: CropData }>();
  for (const formValues of blendFormValues) {
    if (formValues.cropOrBlendSelected == null) {
      continue;
    }
    if (formValues.cropOrBlendSelected.type === 'crop') {
      continue;
    }

    const { itemCount, itemCountValidation, countUnit, cropOrBlendSelected: blendSelected } = formValues;
    const itemCountNum = Number.parseFloat(itemCount) || 0;
    const shouldSetToZero = itemCountValidation !== '' || itemCountNum <= 0;

    const { size, unit } = getSizeParamsFromKey(countUnit);
    if (unit === TRAYS) {
      continue; // Blends cannot be in trays, so skip this
    }
    const realItemCount = Decimal.mul(itemCountNum, size || 1).toNumber(); // Total ounces or grams;

    // Go through each crop
    const crops = blendSelected.crops.edges.map(({ node }) => node);
    for (const { crop, percent_of_blend } of crops) {
      if (crop == null) {
        continue;
      }
      const maybeExistingCrop = cropMap.get(crop.id);
      const existingCount = maybeExistingCrop?.trayCount ?? new Decimal(0);
      if (shouldSetToZero) {
        cropMap.set(crop.id, {
          trayCount: existingCount,
          cropData: crop,
        });
        continue;
      }
      const yieldPerTrayGramsOrOunces = utils.convert({
        inputUnit: crop.yield_unit,
        outputUnit: unit,
        quantity: Number.parseFloat(crop.yield_per_tray),
      });
      const traysNeeded = Decimal.div(realItemCount, yieldPerTrayGramsOrOunces).mul(Decimal.div(percent_of_blend, 100));
      const newCount = existingCount.add(traysNeeded);

      cropMap.set(crop.id, {
        trayCount: newCount,
        cropData: crop,
      });
    }
  }

  return cropMap;
};

export type TraysFromForms = Map<
  string,
  {
    readonly trayCount: Decimal;
    readonly traysWithoutBuffer: Decimal;
    readonly buffer: Decimal;
    readonly cropData: CropData;
  }
>;
export const getTraysFromCropsAndBlends = (
  cropOrBlendForms: React.ReactElement<NewCropOrBlendToUseFormProps>[],
): TraysFromForms => {
  const traysFromCrops = getTraysFromCropForms(cropOrBlendForms);
  const traysFromBlends = getTraysFromBlendForms(cropOrBlendForms);
  const traysFromCropsAndBlends = new Map<string, { readonly trayCount: Decimal; readonly cropData: CropData }>();
  for (const [cropId, traysFromCrop] of traysFromCrops) {
    const maybeExistingTrays = traysFromCropsAndBlends.get(cropId);
    if (maybeExistingTrays) {
      const existingCount = maybeExistingTrays.trayCount;
      const newCount = existingCount.add(traysFromCrop.trayCount);
      traysFromCropsAndBlends.set(cropId, {
        trayCount: newCount,
        cropData: traysFromCrop.cropData,
      });
    } else {
      traysFromCropsAndBlends.set(cropId, traysFromCrop);
    }
  }
  for (const [cropId, traysFromBlend] of traysFromBlends) {
    const maybeExistingTrays = traysFromCropsAndBlends.get(cropId);
    if (maybeExistingTrays) {
      const existingCount = maybeExistingTrays.trayCount;
      const newCount = existingCount.add(traysFromBlend.trayCount);
      traysFromCropsAndBlends.set(cropId, {
        trayCount: newCount,
        cropData: traysFromBlend.cropData,
      });
    } else {
      traysFromCropsAndBlends.set(cropId, traysFromBlend);
    }
  }

  // Add buffers
  const result = new Map<
    string,
    {
      readonly trayCount: Decimal;
      readonly traysWithoutBuffer: Decimal;
      readonly buffer: Decimal;
      readonly cropData: CropData;
    }
  >();
  for (const [cropId, data] of traysFromCropsAndBlends) {
    const existingCount = data.trayCount;
    const buffer = Decimal.mul(existingCount, Decimal.div(data.cropData.buffer_percentage, 100));
    const trayCount = existingCount.add(buffer);
    result.set(cropId, {
      trayCount,
      buffer,
      traysWithoutBuffer: existingCount,
      cropData: data.cropData,
    });
  }

  return result;
};
